import React from 'react';
import './FinancialChart.css';

function FinancialChart({ 
  selectedMetric = 'equity', 
  data, 
  title = 'Financial Trend' 
}) {
  // Generate sample time series data based on the selected metric
  const generateChartData = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    const baseValue = data.rawRatios[getMetricValue(selectedMetric)] || 0;
    
    // Ensure we have a valid numeric value
    const safeBaseValue = typeof baseValue === 'number' && !isNaN(baseValue) ? Math.abs(baseValue) : 1000;
    
    return months.map((month, index) => ({
      month,
      value: safeBaseValue * (0.8 + Math.random() * 0.4), // Simulate variation
      target: safeBaseValue * (1 + (index * 0.05)) // Simulated growth trend
    }));
  };

  const getMetricValue = (metric) => {
    const mapping = {
      'assets': 'totalAssets',
      'liabilities': 'totalLiabilities', 
      'equity': 'totalEquity',
      'income': 'totalIncome',
      'expenses': 'totalExpenses',
      'netIncome': 'netIncome'
    };
    return mapping[metric] || 'totalEquity';
  };

  const getMetricLabel = (metric) => {
    const labels = {
      'assets': 'Total Assets',
      'liabilities': 'Total Liabilities',
      'equity': 'Net Worth (Equity)',
      'income': 'Monthly Income',
      'expenses': 'Monthly Expenses', 
      'netIncome': 'Net Income'
    };
    return labels[metric] || 'Total Equity';
  };

  const getMetricColor = (metric) => {
    const colors = {
      'assets': '#10b981',
      'liabilities': '#f59e0b',
      'equity': '#7c3aed',
      'income': '#059669',
      'expenses': '#ef4444',
      'netIncome': '#3b82f6'
    };
    return colors[metric] || '#7c3aed';
  };

  const chartData = generateChartData();
  const maxValue = Math.max(...chartData.map(d => Math.max(d.value, d.target)));
  const minValue = Math.min(...chartData.map(d => Math.min(d.value, d.target)));
  const range = maxValue - minValue;
  const padding = range * 0.1;

  const getYPosition = (value) => {
    return 180 - ((value - minValue + padding) / (range + 2 * padding)) * 160;
  };

  const formatValue = (value) => {
    // Handle undefined, null, or NaN values
    if (value == null || isNaN(value)) {
      return '$0';
    }
    
    const absValue = Math.abs(value);
    if (absValue >= 1000000) return `$${(absValue / 1000000).toFixed(1)}M`;
    if (absValue >= 1000) return `$${(absValue / 1000).toFixed(1)}K`;
    return `$${absValue.toFixed(0)}`;
  };

  return (
    <div className="financial-chart">
      <div className="chart-header">
        <h3 className="chart-title">{getMetricLabel(selectedMetric)} Trend</h3>
        <div className="chart-legend">
          <div className="legend-item">
            <div className="legend-color actual" style={{ backgroundColor: getMetricColor(selectedMetric) }}></div>
            <span>Actual</span>
          </div>
          <div className="legend-item">
            <div className="legend-color target"></div>
            <span>Target</span>
          </div>
        </div>
      </div>
      
      <div className="chart-container">
        <svg viewBox="0 0 400 200" className="chart-svg">
          {/* Grid lines */}
          <defs>
            <pattern id="grid" width="66.67" height="40" patternUnits="userSpaceOnUse">
              <path d="M 66.67 0 L 0 0 0 40" fill="none" stroke="var(--chart-grid)" strokeWidth="0.5" opacity="0.3"/>
            </pattern>
          </defs>
          <rect width="400" height="200" fill="url(#grid)" />
          
          {/* Data lines */}
          <polyline
            fill="none"
            stroke={getMetricColor(selectedMetric)}
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            points={chartData.map((d, i) => `${50 + i * 60},${getYPosition(d.value)}`).join(' ')}
          />
          
          <polyline
            fill="none"
            stroke="var(--chart-target)"
            strokeWidth="2"
            strokeDasharray="5,5"
            strokeLinecap="round"
            strokeLinejoin="round"
            points={chartData.map((d, i) => `${50 + i * 60},${getYPosition(d.target)}`).join(' ')}
          />
          
          {/* Data points */}
          {chartData.map((d, i) => (
            <g key={i}>
              <circle
                cx={50 + i * 60}
                cy={getYPosition(d.value)}
                r="4"
                fill={getMetricColor(selectedMetric)}
                className="data-point"
              />
              <circle
                cx={50 + i * 60}
                cy={getYPosition(d.target)}
                r="3"
                fill="var(--chart-target)"
                className="data-point target"
              />
            </g>
          ))}
          
          {/* X-axis labels */}
          {chartData.map((d, i) => (
            <text
              key={i}
              x={50 + i * 60}
              y={195}
              textAnchor="middle"
              className="axis-label"
            >
              {d.month}
            </text>
          ))}
        </svg>
      </div>
      
      <div className="chart-summary">
        <div className="summary-stat">
          <span className="stat-label">Current</span>
          <span className="stat-value" style={{ color: getMetricColor(selectedMetric) }}>
            {formatValue(data.rawRatios[getMetricValue(selectedMetric)])}
          </span>
        </div>
        <div className="summary-stat">
          <span className="stat-label">6M Growth</span>
          <span className="stat-value positive">+12.4%</span>
        </div>
        <div className="summary-stat">
          <span className="stat-label">Trend</span>
          <span className="stat-value positive">📈 Rising</span>
        </div>
      </div>
    </div>
  );
}

export default FinancialChart; 